import { Autocomplete, Chip, paletteColors, Stack, TextField } from '@evaneos/b2b-ui';
import CircleIcon from '@evaneos/b2b-ui/icons/Circle';
import { SyntheticEvent } from 'react';

import { DefinitionList } from '../../../../design-system/DefinitionList/DefinitionList';
import { Processes, StepName } from '../../../../design-system/types';
import { useChangeStepMutation } from '../../../../resources/dossier/mutations';
import { DossierId } from '../../../../resources/dossier/types';
import {
    useCurrentTaskByDossierId,
    useGetStepOptionsByDossierId,
} from '../../../../resources/dossier/queries';
import {
    ARCHIVE_PROCESS,
    getProcessFromStepName,
    OPERATION_PROCESS,
    SALES_PROCESS,
} from '../../../../domain';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';

type StepNameOption = {
    label: string;
    value: string;
};

export const StepDefinitionEditableItem = ({ dossierId }: { dossierId: DossierId }) => {
    const labelId = 'step-definition';
    const { step } = useCurrentTaskByDossierId(dossierId);
    const { stepOptions, currentStepValue } = useGetStepOptionsByDossierId(dossierId) || {};

    const changeStepMutation = useChangeStepMutation();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const handleChange = (
        _event: SyntheticEvent,
        newValue: StepNameOption | null,
        close: () => void,
    ) => {
        if (!newValue || !step) return;

        if (newValue.value !== step && !changeStepMutation.isPending) {
            changeStepMutation.mutate({ dossierId, nextStep: newValue.value as StepName });

            trackEvent((ampli, defaultProperties) => {
                ampli.saleStepChanged({
                    ...defaultProperties,
                    ...trackedTripProperties,
                });
            });
        }

        close();
    };

    return (
        <DefinitionList.EditableValue
            labelId={labelId}
            popoverWidth="trigger"
            placement="over"
            disablePopoverMinHeight
            disabled={stepOptions?.length === 0}
            editor={({ close }) => (
                <form>
                    <Autocomplete
                        onChange={(_event, newValue) => handleChange(_event, newValue, close)}
                        disablePortal
                        autoHighlight
                        autoComplete
                        options={stepOptions ?? []}
                        aria-labelledby={labelId}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        sx={{
                            '& .MuiInputBase-root': {
                                background: paletteColors.neutral[100],
                            },
                            '& .MuiInputBase-input': { fontSize: '1.4rem' },
                            '& .MuiInputLabel-root': { fontSize: '1.4rem' },
                        }}
                        slotProps={{
                            paper: {
                                sx: { '& .MuiAutocomplete-option': { fontSize: '1.4rem' } },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                autoFocus
                                sx={{ fontSize: '1.4rem' }}
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        startAdornment: (
                                            <StepChip stepNameValue={currentStepValue} />
                                        ),
                                    },
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <Stack
                                component="li"
                                height="40px"
                                direction="row"
                                {...props}
                                key={option.value}
                            >
                                <StepChip stepNameValue={option} />
                            </Stack>
                        )}
                        disableClearable
                        size="small"
                        open
                        popupIcon={null}
                    />
                </form>
            )}
        >
            <StepChip stepNameValue={currentStepValue} />
        </DefinitionList.EditableValue>
    );
};

const StepChip = ({ stepNameValue }: { stepNameValue: StepNameOption | undefined }) => {
    const colorMap: Record<Processes, Record<string, string>> = {
        [SALES_PROCESS]: paletteColors.blue,
        [OPERATION_PROCESS]: paletteColors.green,
        [ARCHIVE_PROCESS]: paletteColors.neutral,
    };

    const process = stepNameValue?.value
        ? getProcessFromStepName(stepNameValue.value as StepName)
        : SALES_PROCESS;
    const color = colorMap[process];
    return (
        <Chip
            label={stepNameValue?.label}
            icon={
                <CircleIcon
                    aria-hidden="true"
                    sx={{ fontSize: '6px !important', fill: color[900] }}
                />
            }
            size="small"
            variant="rounded"
            sx={{
                paddingLeft: 0.5,
                fontWeight: 'normal',
                backgroundColor: color[100],
                color: color[900],
            }}
        />
    );
};
